import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, User, Check } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DAYS = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];
const TIME_SLOTS = {
  'Lundi': ['09:00', '10:30', '12:00', '16:30', '17:30', '18:30', '19:30'],
  'Mardi': ['09:00', '10:30', '12:00', '16:30', '17:30', '18:30', '19:30'],
  'Mercredi': ['09:00', '10:30', '12:00', '16:30', '17:30', '18:30', '19:30'],
  'Jeudi': ['09:00', '10:30', '12:00', '16:30', '17:30', '18:30', '19:30'],
  'Vendredi': ['09:00', '10:30', '12:00', '16:30', '17:30', '18:30', '19:30'], // Après-midi maintenant ouvert
  'Samedi': ['09:00', '10:30', '12:00'], // Matin seulement
  'Dimanche': ['09:00', '10:30', '12:00'] // Matin seulement - Nouveau !
};
const ALL_TIME_SLOTS = ['09:00', '10:30', '12:00', '16:30', '17:30', '18:30', '19:30'];


// Plus de fermeture permanente - tous les jours suivent leurs horaires normaux

export default function WeeklyCalendar({ token }) {
  const [currentWeek, setCurrentWeek] = useState('');
  const [availabilities, setAvailabilities] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [showBookingDialog, setShowBookingDialog] = useState(false);
  const [showMoveDialog, setShowMoveDialog] = useState(false);
  const [showAddClientDialog, setShowAddClientDialog] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [selectedClient, setSelectedClient] = useState('');
  const [newDay, setNewDay] = useState('');
  const [newTimeSlot, setNewTimeSlot] = useState('');
  const [addClientSlot, setAddClientSlot] = useState(null);

  useEffect(() => {
    setCurrentWeek(getNextMonday());
    fetchClients();
  }, []);

  useEffect(() => {
    if (currentWeek) {
      fetchWeekData();
    }
  }, [currentWeek]);

  const getNextMonday = () => {
    const today = new Date();
    const dayOfWeek = today.getDay(); // 0=Sunday, 1=Monday, ..., 6=Saturday
    const hour = today.getHours();
    
    let daysUntilMonday;
    
    // PENDANT LA FENÊTRE DE RÉSERVATION (Samedi 11h → Dimanche 20h)
    // On réserve pour LA SEMAINE PROCHAINE
    if ((dayOfWeek === 6 && hour >= 11) || dayOfWeek === 0) {
      // Samedi après 11h OU Dimanche (peu importe l'heure jusqu'à 20h)
      // → Aller au PROCHAIN lundi
      if (dayOfWeek === 6) {
        // Samedi → Lundi suivant = +2 jours
        daysUntilMonday = 2;
      } else {
        // Dimanche → Lundi suivant = +1 jour
        daysUntilMonday = 1;
      }
    } 
    // HORS FENÊTRE DE RÉSERVATION
    // On affiche la semaine actuelle
    else if (dayOfWeek === 1) {
      // Lundi → ce lundi
      daysUntilMonday = 0;
    } else if (dayOfWeek === 0) {
      // Dimanche avant réouverture → semaine actuelle
      daysUntilMonday = 1;
    } else {
      // Mardi-Vendredi ou Samedi avant 11h → Lundi de cette semaine
      daysUntilMonday = -(dayOfWeek - 1);
    }
    
    const monday = new Date(today);
    monday.setDate(today.getDate() + daysUntilMonday);
    return monday.toISOString().split('T')[0];
  };

  const formatWeekRange = (mondayDate) => {
    // Parse date in UTC to avoid timezone issues
    const [year, month, day] = mondayDate.split('-').map(Number);
    const monday = new Date(year, month - 1, day); // month is 0-indexed
    const sunday = new Date(year, month - 1, day + 6);
    
    const options = { day: 'numeric', month: 'short' };
    return `${monday.toLocaleDateString('fr-FR', options)} - ${sunday.toLocaleDateString('fr-FR', options)}`;
  };

  const changeWeek = (direction) => {
    const [year, month, day] = currentWeek.split('-').map(Number);
    const current = new Date(year, month - 1, day);
    current.setDate(current.getDate() + (direction * 7));
    
    // Format back to YYYY-MM-DD
    const newYear = current.getFullYear();
    const newMonth = String(current.getMonth() + 1).padStart(2, '0');
    const newDay = String(current.getDate()).padStart(2, '0');
    setCurrentWeek(`${newYear}-${newMonth}-${newDay}`);
  };

  const fetchClients = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/clients`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setClients(data.clients.filter(c => c.is_active));
      }
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  const fetchWeekData = async () => {
    setLoading(true);
    try {
      // Fetch availabilities
      const availRes = await fetch(`${API_URL}/api/admin/availability/${currentWeek}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (availRes.ok) {
        const availData = await availRes.json();
        setAvailabilities(availData.clients || []);
      }

      // Fetch bookings
      const bookRes = await fetch(`${API_URL}/api/admin/bookings/${currentWeek}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (bookRes.ok) {
        const bookData = await bookRes.json();
        setBookings(bookData.bookings || []);
      }
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  const getSlotData = (day, time) => {
    // Check if there are confirmed bookings (can be multiple clients up to 4)
    const slotBookings = bookings.filter(b => b.day === day && b.time_slot === time);
    if (slotBookings.length > 0) {
      return { type: 'booking', data: slotBookings };
    }

    // Check if there are availabilities
    const clientsAvailable = availabilities.filter(a => 
      a.availabilities.some(av => av.day === day && av.time_slot === time)
    );

    if (clientsAvailable.length > 0) {
      return { type: 'available', data: clientsAvailable };
    }

    return { type: 'empty', data: null };
  };

  const handleSlotClick = (day, time) => {
    const slotData = getSlotData(day, time);
    
    if (slotData.type === 'booking') {
      // Already booked - do nothing, use the action buttons instead
      return;
    } else if (slotData.type === 'available') {
      // Show booking dialog
      setSelectedSlot({ day, time, clients: slotData.data });
      setShowBookingDialog(true);
    } else {
      toast.error('Aucun client disponible à ce créneau');
    }
  };

  const handleMoveBooking = (booking) => {
    setSelectedBooking(booking);
    setNewDay('');
    setNewTimeSlot('');
    setShowMoveDialog(true);
  };

  const handleConfirmMove = async () => {
    if (!newDay || !newTimeSlot) {
      toast.error('Sélectionnez un nouveau créneau');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/admin/bookings/${selectedBooking.id}/move`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          new_day: newDay,
          new_time_slot: newTimeSlot
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Erreur lors du déplacement');
      }

      toast.success(`Réservation déplacée: ${data.old_slot} → ${data.new_slot}`);
      setShowMoveDialog(false);
      setSelectedBooking(null);
      fetchWeekData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleDeleteBooking = async (booking) => {
    if (!confirm(`Supprimer la réservation de ${booking.client.first_name} ${booking.client.last_name} ?`)) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/admin/bookings/${booking.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Erreur de suppression');

      toast.success('Réservation supprimée et séance restituée');
      fetchWeekData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleCreateBooking = async () => {
    if (!selectedClient) {
      toast.error('Sélectionnez un client');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/admin/bookings/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          client_id: selectedClient,
          week_start_date: currentWeek,
          day: selectedSlot.day,
          time_slot: selectedSlot.time
        })
      });

      if (!response.ok) throw new Error('Erreur de création');

      toast.success('Réservation créée !');
      setShowBookingDialog(false);
      setSelectedClient('');
      fetchWeekData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleAddClientManually = (day, time) => {
    setAddClientSlot({ day, time });
    setShowAddClientDialog(true);
  };

  const handleConfirmAddClient = async () => {
    if (!selectedClient) {
      toast.error('Sélectionnez un client');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/admin/bookings/manual`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          client_id: selectedClient,
          week_start_date: currentWeek,
          day_of_week: addClientSlot.day,
          time_slot: addClientSlot.time
        })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Erreur lors de l\'ajout');
      }

      const data = await response.json();
      toast.success(`Client ajouté ! ${data.remaining_sessions} séances restantes`);
      setShowAddClientDialog(false);
      setSelectedClient('');
      fetchWeekData();
      fetchClients(); // Refresh clients to update session counts
    } catch (error) {
      toast.error(error.message);
    }
  };

  const renderSlot = (day, time) => {
    // Check if this time slot is available for this day
    const daySlots = TIME_SLOTS[day] || [];
    if (!daySlots.includes(time)) {
      // Slot not available for this day (e.g., afternoon on Saturday, or any time on Sunday)
      return (
        <div className="h-full bg-zinc-950 border border-zinc-900 rounded opacity-50">
          <div className="h-full flex items-center justify-center text-zinc-700 text-xs">
            Fermé
          </div>
        </div>
      );
    }

    const slotData = getSlotData(day, time);
    
    if (slotData.type === 'booking') {
      const bookings = slotData.data; // Now an array of bookings
      return (
        <div 
          className="h-full bg-green-900/40 border border-green-700 rounded p-2 group hover:bg-green-900/60 transition-colors"
        >
          <div className="flex flex-col gap-2">
            <div className="flex items-start gap-1 mb-1">
              <Check className="w-3 h-3 text-green-400 flex-shrink-0 mt-0.5" />
              <div className="min-w-0 flex-1">
                <p className="text-xs font-semibold text-green-300">
                  {bookings.map((b, i) => (
                    <span key={b.id}>
                      {b.client.first_name}
                      {i < bookings.length - 1 && ', '}
                    </span>
                  ))}
                </p>
                <p className="text-xs text-green-400/70">
                  {bookings.length} / 4 confirmé{bookings.length > 1 ? 's' : ''}
                </p>
              </div>
            </div>
            
            {/* Action buttons for each booking */}
            <div className="space-y-1 max-h-20 overflow-y-auto">
              {bookings.map(booking => (
                <div key={booking.id} className="flex items-center justify-between gap-1 bg-green-900/20 rounded px-1 py-0.5">
                  <span className="text-xs text-green-200 truncate">
                    {booking.client.first_name}
                  </span>
                  <div className="flex gap-1">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMoveBooking(booking);
                      }}
                      className="p-0.5 bg-blue-600 hover:bg-blue-700 rounded text-white"
                      title="Déplacer"
                    >
                      <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                      </svg>
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteBooking(booking);
                      }}
                      className="p-0.5 bg-red-600 hover:bg-red-700 rounded text-white"
                      title="Supprimer"
                    >
                      <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Add Client Button - only show if less than 4 */}
            {bookings.length < 4 && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleAddClientManually(day, time);
                }}
                className="w-full text-xs bg-green-600/20 hover:bg-green-600/40 border border-green-600/50 rounded py-1 text-green-300 transition-colors"
              >
                + Ajouter un client ({bookings.length}/4)
              </button>
            )}
          </div>
        </div>
      );
    }
    
    if (slotData.type === 'available') {
      const clientsCount = slotData.data.length;
      return (
        <div 
          className="h-full bg-blue-900/30 border border-blue-700 rounded p-2 cursor-pointer hover:bg-blue-900/50 transition-colors"
          onClick={() => handleSlotClick(day, time)}
        >
          <div className="flex items-start gap-1">
            <User className="w-3 h-3 text-blue-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-semibold text-blue-300">
                {clientsCount} dispo{clientsCount > 1 ? 's' : ''}
              </p>
              <p className="text-xs text-blue-400/70">
                Cliquer pour réserver
              </p>
            </div>
          </div>
        </div>
      );
    }
    
    return (
      <div 
        className="h-full bg-zinc-900/50 border border-zinc-800 rounded hover:border-zinc-700 transition-colors p-2 cursor-pointer group"
        onClick={() => handleAddClientManually(day, time)}
      >
        <button className="w-full h-full text-xs text-zinc-500 hover:text-zinc-300 transition-colors">
          + Ajouter un client
        </button>
      </div>
    );
  };

  return (
    <>
      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle className="text-white">Planning Hebdomadaire</CardTitle>
              <CardDescription className="text-zinc-400">
                {formatWeekRange(currentWeek)}
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => changeWeek(-1)}
                className="bg-zinc-800 border-zinc-700"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentWeek(getNextMonday())}
                className="bg-zinc-800 border-zinc-700 text-white"
              >
                Cette semaine
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => changeWeek(1)}
                className="bg-zinc-800 border-zinc-700"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-12 text-zinc-400">Chargement...</div>
          ) : (
            <div className="overflow-x-auto">
              <div className="min-w-[800px]">
                {/* Header */}
                <div className="grid grid-cols-8 gap-2 mb-2">
                  <div className="text-xs font-semibold text-zinc-500 p-2">Horaire</div>
                  {DAYS.map(day => (
                    <div key={day} className="text-xs font-semibold text-zinc-300 text-center p-2">
                      {day}
                    </div>
                  ))}
                </div>

                {/* Time slots */}
                <div className="space-y-2">
                  {ALL_TIME_SLOTS.map(time => (
                    <div key={time} className="grid grid-cols-8 gap-2">
                      <div className="flex items-center justify-center bg-zinc-900 border border-zinc-800 rounded p-2">
                        <span className="text-sm font-mono text-zinc-400">{time}</span>
                      </div>
                      {DAYS.map(day => (
                        <div key={`${day}-${time}`} className="min-h-[70px]">
                          {renderSlot(day, time)}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Legend */}
          <div className="mt-6 flex gap-6 text-xs">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-green-900/40 border border-green-700 rounded"></div>
              <span className="text-zinc-400">Réservé</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-blue-900/30 border border-blue-700 rounded"></div>
              <span className="text-zinc-400">Disponibilités soumises</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-zinc-900/50 border border-zinc-800 rounded"></div>
              <span className="text-zinc-400">Vide</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Booking Dialog */}
      <Dialog open={showBookingDialog} onOpenChange={setShowBookingDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white">
          <DialogHeader>
            <DialogTitle>Créer une réservation</DialogTitle>
            <DialogDescription className="text-zinc-400">
              {selectedSlot?.day} - {selectedSlot?.time}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <label className="text-sm text-zinc-300 mb-2 block">
                Clients disponibles à ce créneau :
              </label>
              <Select value={selectedClient} onValueChange={setSelectedClient}>
                <SelectTrigger className="bg-zinc-800 border-zinc-700">
                  <SelectValue placeholder="Sélectionner un client" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  {selectedSlot?.clients.map(item => (
                    <SelectItem key={item.client.email} value={item.client.id || item.client.email}>
                      {item.client.first_name} {item.client.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setShowBookingDialog(false)}
                className="flex-1 border-zinc-700"
              >
                Annuler
              </Button>
              <Button
                onClick={handleCreateBooking}
                className="flex-1 bg-red-600 hover:bg-red-700"
              >
                Confirmer la réservation
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Move Booking Dialog */}
      <Dialog open={showMoveDialog} onOpenChange={setShowMoveDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white">
          <DialogHeader>
            <DialogTitle>Déplacer la réservation</DialogTitle>
            <DialogDescription className="text-zinc-400">
              Client: {selectedBooking?.client.first_name} {selectedBooking?.client.last_name}
              <br />
              Actuel: {selectedBooking?.day} - {selectedBooking?.time_slot}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm text-zinc-300">Nouveau jour</label>
              <Select value={newDay} onValueChange={setNewDay}>
                <SelectTrigger className="bg-zinc-800 border-zinc-700">
                  <SelectValue placeholder="Sélectionner un jour" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  {DAYS.map(day => (
                    <SelectItem key={day} value={day}>{day}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm text-zinc-300">Nouvel horaire</label>
              <Select value={newTimeSlot} onValueChange={setNewTimeSlot}>
                <SelectTrigger className="bg-zinc-800 border-zinc-700">
                  <SelectValue placeholder="Sélectionner un horaire" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  {(TIME_SLOTS[newDay] || []).map(time => (
                    <SelectItem key={time} value={time}>{time}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setShowMoveDialog(false)}
                className="flex-1 border-zinc-700"
              >
                Annuler
              </Button>
              <Button
                onClick={handleConfirmMove}
                className="flex-1 bg-red-600 hover:bg-red-700"
              >
                Déplacer
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Client Manually Dialog */}
      <Dialog open={showAddClientDialog} onOpenChange={setShowAddClientDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-md">
          <DialogHeader>
            <DialogTitle>Ajouter un client manuellement</DialogTitle>
            <DialogDescription className="text-zinc-400">
              {addClientSlot && `${addClientSlot.day} - ${addClientSlot.time}`}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <label className="text-sm text-zinc-300 mb-2 block">
                Sélectionner un client :
              </label>
              <Select value={selectedClient} onValueChange={setSelectedClient}>
                <SelectTrigger className="bg-zinc-800 border-zinc-700">
                  <SelectValue placeholder="Choisir un client..." />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  {clients.filter(c => c.is_active).map(client => (
                    <SelectItem key={client.id} value={client.id}>
                      {client.first_name} {client.last_name} ({client.remaining_sessions} séances)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-zinc-500 mt-2">
                Tous les clients actifs peuvent être ajoutés manuellement
              </p>
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setShowAddClientDialog(false);
                  setSelectedClient('');
                }}
                className="flex-1 border-zinc-700"
              >
                Annuler
              </Button>
              <Button
                onClick={handleConfirmAddClient}
                disabled={!selectedClient}
                className="flex-1 bg-red-600 hover:bg-red-700 disabled:opacity-50"
              >
                Ajouter
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

